import {
  Boxes,
  Calculator,
  CalendarClock,
  ChartColumn,
  FileSignature,
  FileSpreadsheet,
  FolderLock,
  Gavel,
  GraduationCap,
  Handshake,
  HardHat,
  LifeBuoy,
  Megaphone,
  Ruler,
  Scale,
  ShieldCheck,
  ShoppingCart,
  SquareKanban,
  Store,
  Target,
  Truck,
  Users,
  Wallet,
  Wrench,
} from 'lucide-react';

/**
 * Catalogue des 24 modules de l'ecosysteme Nexus.
 *
 * Source unique de la landing publique : le bandeau defilant, la section
 * « Disponibles » et la grille par domaine lisent toutes ce tableau.
 *
 * Volontairement statique. Cette page est un build CRA servi depuis
 * `httpdocs`, et son API (`/api/apps/pricing`) ne renvoie que les apps
 * `active` — les 22 modules a venir n'y figurent pas.
 *
 * `domain` est une CLE, pas un libelle : les noms de domaine sont traduits
 * (DOMAIN_ORDER + i18n `landing.domains.*`) pour que la page bascule en
 * anglais sans dupliquer le catalogue.
 */

/** Accent par domaine. Un module herite de son domaine, jamais d'une couleur propre. */
export const DOMAIN_ACCENT = {
  commercial: '#E8B48F',
  operations: '#86D6B4',
  finance: '#8FB6E8',
  conformite: '#C7A8E8',
  ressources: '#D9DE94',
};

/** Meme teinte a 15 % : fond des pastilles d'icone. */
export const DOMAIN_TINT = {
  commercial: 'rgba(232,180,143,0.15)',
  operations: 'rgba(134,214,180,0.15)',
  finance: 'rgba(143,182,232,0.15)',
  conformite: 'rgba(199,168,232,0.15)',
  ressources: 'rgba(217,222,148,0.15)',
};

/** Ordre d'affichage des domaines dans la grille de l'ecosysteme. */
export const DOMAIN_ORDER = [
  'commercial',
  'operations',
  'finance',
  'conformite',
  'ressources',
];

/**
 * Les 24 modules. `live: true` = app en production, joignable via `url`.
 * Les autres sont annonces « En developpement » et n'exposent aucun lien.
 */
export const MODULES = [
  {
    slug: 'nexus-compta',
    name: 'Nexus Compta',
    domain: 'finance',
    icon: Calculator,
    live: true,
    url: 'https://compta.nexus-360.fr',
  },
  {
    slug: 'nexus-sirh',
    name: 'Nexus SIRH',
    domain: 'ressources',
    icon: Users,
    live: true,
    url: 'https://sirh.nexus-360.fr',
  },
  { slug: 'nexus-crm', name: 'Nexus CRM', domain: 'commercial', icon: Target },
  { slug: 'nexus-support', name: 'Nexus Support', domain: 'commercial', icon: LifeBuoy },
  { slug: 'nexus-marketing', name: 'Nexus Marketing', domain: 'commercial', icon: Megaphone },
  { slug: 'nexus-caisse', name: 'Nexus Caisse', domain: 'commercial', icon: Store },
  { slug: 'nexus-appels-offres', name: "Nexus Appels d'offres", domain: 'commercial', icon: Gavel },
  { slug: 'nexus-metres', name: 'Nexus Métrés', domain: 'commercial', icon: Ruler },
  { slug: 'nexus-chantiers', name: 'Nexus Chantiers', domain: 'operations', icon: HardHat },
  { slug: 'nexus-projets', name: 'Nexus Projets', domain: 'operations', icon: SquareKanban },
  { slug: 'nexus-planning', name: 'Nexus Planning', domain: 'operations', icon: CalendarClock },
  { slug: 'nexus-stock', name: 'Nexus Stock', domain: 'operations', icon: Boxes },
  { slug: 'nexus-achats', name: 'Nexus Achats', domain: 'operations', icon: ShoppingCart },
  { slug: 'nexus-situations', name: 'Nexus Situations', domain: 'finance', icon: FileSpreadsheet },
  { slug: 'nexus-tresorerie', name: 'Nexus Trésorerie', domain: 'finance', icon: Wallet },
  { slug: 'nexus-bi', name: 'Nexus BI', domain: 'finance', icon: ChartColumn },
  { slug: 'nexus-docs', name: 'Nexus Docs', domain: 'conformite', icon: FolderLock },
  { slug: 'nexus-contrats', name: 'Nexus Contrats', domain: 'conformite', icon: FileSignature },
  { slug: 'nexus-sous-traitance', name: 'Nexus Sous-traitance', domain: 'conformite', icon: Handshake },
  { slug: 'nexus-qualite', name: 'Nexus Qualité', domain: 'conformite', icon: ShieldCheck },
  { slug: 'nexus-juridique', name: 'Nexus Juridique', domain: 'conformite', icon: Scale },
  { slug: 'nexus-materiel', name: 'Nexus Matériel', domain: 'ressources', icon: Wrench },
  { slug: 'nexus-flotte', name: 'Nexus Flotte', domain: 'ressources', icon: Truck },
  { slug: 'nexus-formation', name: 'Nexus Formation', domain: 'ressources', icon: GraduationCap },
];

/** Les deux modules reellement en production. */
export const LIVE_MODULES = MODULES.filter((m) => m.live);

/** Les 22 modules annonces. */
export const UPCOMING_MODULES = MODULES.filter((m) => !m.live);
